import { useState, useEffect } from 'react';
import { studentAPI } from '@/services/api';
import { getGradeColor } from '@/utils/helpers';
import toast from 'react-hot-toast';

const StudentResults = () => {
  const [term, setTerm] = useState('1st');
  const [session, setSession] = useState('2025/2026');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await studentAPI.getResults();
        setResults(res.data.data || []);
      } catch (err) {
        console.error('Results fetch error:', err);
        toast.error('Failed to load results');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  const totalScore = results.reduce((sum, r) => sum + Number(r.total), 0);
  const average = results.length > 0 ? (totalScore / results.length).toFixed(2) : '0.00';
  const highest = results.length > 0 ? Math.max(...results.map((r) => Number(r.total))) : 0;
  const lowest = results.length > 0 ? Math.min(...results.map((r) => Number(r.total))) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-black text-slate-800">My Results</h2>
        <button className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm flex items-center gap-2 self-start sm:self-auto">
          <i className="fas fa-download" /> Download Result
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Term</label>
          <select
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            <option value="1st">1st Term</option>
            <option value="2nd">2nd Term</option>
            <option value="3rd">3rd Term</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Session</label>
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            <option value="2025/2026">2025/2026</option>
            <option value="2024/2025">2024/2025</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        {[
          { label: 'Average Score', value: average, icon: 'fa-chart-line', color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Total Subjects', value: results.length, icon: 'fa-book', color: 'text-blue-600 bg-blue-50' },
          { label: 'Highest Score', value: highest, icon: 'fa-arrow-up', color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Lowest Score', value: lowest, icon: 'fa-arrow-down', color: 'text-amber-600 bg-amber-50' },
        ].map((stat, idx) => (
          <div key={idx} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${stat.color}`}>
              <i className={`fas ${stat.icon}`} />
            </div>
            <p className="text-2xl font-black text-slate-800">{stat.value}</p>
            <p className="text-xs text-slate-500 font-medium mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <i className="fas fa-table text-emerald-500" /> Result Details — {term} Term, {session}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Subject</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">CA Score</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Exam Score</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Total</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Grade</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Remark</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.length > 0 ? results.map((result, idx) => {
                const remark = result.grade === 'A' ? 'Excellent' : result.grade === 'B' ? 'Very Good' : result.grade === 'C' ? 'Good' : result.grade === 'D' ? 'Fair' : result.grade === 'E' ? 'Pass' : 'Fail';
                return (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-slate-800 text-sm">{result.subject}</td>
                    <td className="px-6 py-4 text-center text-slate-600 text-sm">{result.ca_score}</td>
                    <td className="px-6 py-4 text-center text-slate-600 text-sm">{result.exam_score}</td>
                    <td className="px-6 py-4 text-center font-bold text-slate-800 text-sm">{result.total}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getGradeColor(result.grade)}`}>
                        {result.grade}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{remark}</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                    <i className="fas fa-inbox text-3xl mb-3 block" />
                    <p className="text-sm">No results found</p>
                  </td>
                </tr>
              )}
            </tbody>
            {results.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr>
                  <td className="px-6 py-4 font-bold text-slate-800 text-sm">Overall</td>
                  <td className="px-6 py-4 text-center font-bold text-slate-800 text-sm">{results.reduce((s, r) => s + Number(r.ca_score), 0)}</td>
                  <td className="px-6 py-4 text-center font-bold text-slate-800 text-sm">{results.reduce((s, r) => s + Number(r.exam_score), 0)}</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600 text-sm">{totalScore}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-emerald-600 bg-emerald-50">
                      {average >= 70 ? 'A' : average >= 60 ? 'B' : average >= 50 ? 'C' : average >= 45 ? 'D' : average >= 40 ? 'E' : 'F'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-emerald-600">{average >= 50 ? 'Promoted' : 'Repeat'}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudentResults;

